import React, { useState } from 'react';
import { X, KeyRound, AlertTriangle, Eye, EyeOff, CheckCircle2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

import caregiverService from '../../services/caregiverService';

/**
 * Reset PIN modal (FECOA-1373). Issues a system-generated 6-digit temp PIN,
 * displays it once on screen, never emails. SC must communicate out-of-band.
 *
 * Props:
 *   caregiverId
 *   caregiverLabel  optional name + caregiver_id displayed in the body
 *   onClose
 *   onResetComplete  called after the SC clicks "Done — PIN Communicated"
 */
const ResetPinModal = ({ caregiverId, caregiverLabel, onClose, onResetComplete }) => {
  const [loading, setLoading] = useState(true);
  const [tempPin, setTempPin] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const data = await caregiverService.resetPin(caregiverId);
        if (!cancelled) setTempPin(data.temporary_pin);
      } catch (err) {
        console.error('reset pin failed', err);
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Failed to reset PIN.');
          toast.error('Failed to reset PIN.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [caregiverId]);

  const masked = tempPin ? '• '.repeat(tempPin.length).trim() : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
      <div className="w-full max-w-md animate-slide-up rounded-xl bg-white p-6 shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Temporary PIN Generated</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="btn btn-ghost btn-sm btn-icon -mr-2 -mt-1"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-xs text-amber-800 dark:text-amber-200">
            This PIN will not be emailed. Read it to the caregiver by phone or write it on their
            appointment card.
          </p>
        </div>

        {caregiverLabel && (
          <div className="mb-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Caregiver</p>
            <p className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{caregiverLabel}</p>
          </div>
        )}

        <div className="mb-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Temporary PIN</p>
          {loading ? (
            <div className="py-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
            </div>
          ) : error ? (
            <p className="py-2 text-sm text-danger-600 dark:text-danger-400">{error}</p>
          ) : (
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-gray-300/80 bg-gray-50 py-1.5 pl-3 pr-1.5 dark:border-white/10 dark:bg-gray-900">
              <span className="select-all font-mono text-xl tracking-widest text-primary-700 dark:text-primary-300">
                {reveal ? tempPin : masked}
              </span>
              <button
                type="button"
                onClick={() => setReveal((r) => !r)}
                className="btn btn-ghost btn-sm btn-icon ml-auto"
                aria-label={reveal ? 'Hide PIN' : 'Show PIN'}
              >
                {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          )}
          <p className="hint">
            The caregiver will be required to set a new PIN on first login.
          </p>
        </div>

        {!loading && !error && (
          <div className="mb-5 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 p-3 dark:border-primary-500/30 dark:bg-primary-500/10">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
            <div>
              <p className="text-xs font-medium text-primary-700 dark:text-primary-300">Audit trail updated</p>
              <p className="text-xs text-primary-700/80 dark:text-primary-400/80">
                PIN reset logged with timestamp and your coordinator ID. The previous PIN has been invalidated.
              </p>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => onResetComplete && onResetComplete()}
          disabled={loading || !!error}
          className="btn btn-primary w-full"
        >
          Done &mdash; PIN Communicated
        </button>
      </div>
    </div>
  );
};

export default ResetPinModal;
